import { useEffect } from 'react';
import { create } from 'zustand';

import i18n from '../i18n/config';
import notify from '../lib/notify';
import projectAnalysisService from '../services/projectAnalysisService';
import { isRunActive, type AnalysisRun } from '../types/projectAnalysis';

/**
 * Project analyses in flight, mirrored from the backend (the analysis runs as a
 * server job, so leaving the page or reloading does not lose it). Polls
 * GET /projects/analysis-runs?active=1: every 2 s while a run is active, every
 * 15 s otherwise. When a run seen active ends, `completions[projectId]` is
 * bumped so the project page reloads its analysis and micro-actions.
 */
interface AnalysisRunsState {
  /** Last poll: active runs and runs finished in the last two minutes. */
  runs: AnalysisRun[];
  /** Run ids hidden from the dock by the user. */
  dismissed: Record<string, true>;
  /** Project id -> number of runs seen ending (a change triggers a reload). */
  completions: Record<string, number>;
  /** Adds a run started from this browser (raises a toast when it ends). */
  track: (run: AnalysisRun) => void;
  poll: () => Promise<void>;
  dismiss: (runId: string) => void;
  clear: () => void;
}

/** Runs started from this browser: only those raise a toast when they end. */
const mine = new Set<string>();
/** Ids of the runs seen active at the previous poll (or tracked since). */
let seenActive = new Set<string>();

const byCreation = (a: AnalysisRun, b: AnalysisRun) => a.created_at.localeCompare(b.created_at);

export const useAnalysisRunsStore = create<AnalysisRunsState>((set, get) => ({
  runs: [],
  dismissed: {},
  completions: {},

  track: (run) => {
    mine.add(run.id);
    if (isRunActive(run)) seenActive.add(run.id);
    set((s) => ({ runs: [...s.runs.filter((r) => r.id !== run.id), run].sort(byCreation) }));
  },

  poll: async () => {
    // Snapshot before fetching: a run tracked while the request is in flight
    // is not in its answer yet and must not be taken as ended.
    const before = new Set(seenActive);
    let list: AnalysisRun[];
    try {
      list = await projectAnalysisService.listRuns({ active: true });
    } catch {
      return; // try again at the next tick
    }
    const byId = new Map(list.map((r) => [r.id, r]));
    const ended: AnalysisRun[] = [];
    const endedProjects: string[] = [];
    for (const id of before) {
      const run = byId.get(id);
      if (run && isRunActive(run)) continue;
      if (run) {
        ended.push(run);
        endedProjects.push(run.project_id);
      } else {
        // Gone from the list (finished long ago): reload the project, no toast.
        const previous = get().runs.find((r) => r.id === id);
        if (previous) endedProjects.push(previous.project_id);
        mine.delete(id);
      }
    }

    const nextActive = new Set(list.filter(isRunActive).map((r) => r.id));
    for (const id of seenActive) if (!before.has(id)) nextActive.add(id);
    seenActive = nextActive;

    set((s) => {
      // Runs tracked meanwhile are kept until the backend lists them.
      const meanwhile = s.runs.filter((r) => !before.has(r.id) && seenActive.has(r.id) && !byId.has(r.id));
      const completions = { ...s.completions };
      for (const projectId of endedProjects) completions[projectId] = (completions[projectId] ?? 0) + 1;
      return { runs: [...list, ...meanwhile].sort(byCreation), completions };
    });

    for (const run of ended) {
      if (!mine.delete(run.id)) continue;
      const title = run.project_title || i18n.t('projects.runs.untitled');
      if (run.status === 'succeeded') notify.success(i18n.t('projects.runs.doneToast', { title }));
      else notify.error(run.error || i18n.t('projects.runs.failedToast', { title }));
    }
  },

  dismiss: (runId) => set((s) => ({ dismissed: { ...s.dismissed, [runId]: true } })),

  clear: () => {
    mine.clear();
    seenActive = new Set();
    set({ runs: [], dismissed: {}, completions: {} });
  },
}));

const FAST_MS = 2_000;
const IDLE_MS = 15_000;

/** Keeps polling while `enabled` (projects module on, user may read projects). Mount once. */
export function useAnalysisRunsPolling(enabled: boolean): void {
  const busy = useAnalysisRunsStore((s) => s.runs.some(isRunActive));
  const poll = useAnalysisRunsStore((s) => s.poll);
  const clear = useAnalysisRunsStore((s) => s.clear);

  useEffect(() => {
    if (!enabled) {
      clear();
      return;
    }
    void poll();
    const timer = window.setInterval(() => void poll(), busy ? FAST_MS : IDLE_MS);
    return () => window.clearInterval(timer);
  }, [enabled, busy, poll, clear]);
}

/** The active run of a project, if any. */
export const useActiveRun = (projectId: string | undefined): AnalysisRun | undefined =>
  useAnalysisRunsStore((s) =>
    projectId ? s.runs.find((r) => r.project_id === projectId && isRunActive(r)) : undefined
  );

/** The latest run of a project in the recent list (active or just finished). */
export const useLatestRun = (projectId: string | undefined): AnalysisRun | undefined =>
  useAnalysisRunsStore((s) => {
    if (!projectId) return undefined;
    for (let i = s.runs.length - 1; i >= 0; i -= 1) if (s.runs[i].project_id === projectId) return s.runs[i];
    return undefined;
  });

/** Bumps each time a run of the project is seen ending. */
export const useRunCompletions = (projectId: string | undefined): number =>
  useAnalysisRunsStore((s) => (projectId ? s.completions[projectId] ?? 0 : 0));
